/**
 * Custom funnel-event tracking on Supabase.
 *
 * Writes one row per event to public.funnel_events. Used for the daily activation
 * scan: signup -> designer_opened -> address_searched -> first_camera_placed
 * -> project_saved -> upgrade_clicked.
 *
 * Privacy:
 * - Authenticated users: tracked under "legitimate interest" (ToS covers product
 *   analytics). Only user_id (UUID) is stored, no PII.
 * - Anonymous users: tracked only if cookie consent was granted.
 *
 * Reliability:
 * - Fire-and-forget: never blocks the UI, never throws.
 * - Failures are silently swallowed (best-effort telemetry, not critical path).
 */
import { supabase } from '@/integrations/supabase/client';
import { hasConsentedToCookies } from '@/components/CookieConsent';

// Cached user id so trackFunnelEvent doesn't serialize through
// supabase-js's auth mutex on every event. A page that emits 4-5 events
// during a route change (page_viewed, feature_used, etc.) would otherwise
// queue 4-5 getSession() calls behind one another, stalling analytics
// during rapid clicks. The cache refreshes on SIGNED_IN/SIGNED_OUT events
// emitted by supabase.auth, so it stays accurate without polling.
let cachedUserId: string | null = null;
let userIdCacheInit = false;

/**
 * Read the persisted Supabase auth token straight out of localStorage so the
 * cache is primed SYNCHRONOUSLY on first event. Without this, the very first
 * trackFunnelEvent in a fresh tab fires before `getSession()` resolves and
 * the user looks anonymous — for a logged-in user without cookie consent,
 * the consent gate then drops the event (bug introduced by the previous
 * "cached userId" fix).
 *
 * The key is the supabase-js storage key: `sb-<projectRef>-auth-token`. It
 * stores a JSON-encoded session object with the access_token JWT. We parse
 * the JWT payload (no signature verification — we trust local storage as
 * already auth-vetted by supabase-js) to get the user id immediately.
 */
function primeUserIdSync(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    // Find the supabase auth-token key. Project ref is in the SUPABASE_URL
    // (https://<ref>.supabase.co), but we can also just scan keys for the
    // `sb-*-auth-token` pattern — exactly one auth-token entry exists.
    let raw: string | null = null;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
        raw = localStorage.getItem(key);
        break;
      }
    }
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const jwt: string | undefined = parsed?.access_token ?? parsed?.currentSession?.access_token;
    if (typeof jwt !== 'string') return null;
    const payload = jwt.split('.')[1];
    if (!payload) return null;
    // JWT payload is base64url. atob handles standard base64; convert.
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json);
    // `sub` is the user UUID. Also sanity-check expiry — an expired token
    // gives wrong attribution; treat as anon and let the async path
    // refresh.
    if (typeof claims?.exp === 'number' && claims.exp * 1000 < Date.now()) return null;
    return typeof claims?.sub === 'string' ? claims.sub : null;
  } catch {
    return null;
  }
}

function ensureUserIdCacheInit(): void {
  if (userIdCacheInit) return;
  userIdCacheInit = true;
  // Prime synchronously from localStorage so the first event in a fresh
  // tab isn't dropped for logged-in users without cookie consent.
  cachedUserId = primeUserIdSync();
  // Async path keeps the cache fresh against refresh-token rotations and
  // the SIGNED_IN/OUT lifecycle.
  supabase.auth.getSession().then(({ data }) => {
    cachedUserId = data.session?.user?.id ?? cachedUserId;
  }).catch(() => { /* best-effort */ });
  supabase.auth.onAuthStateChange((_event, session) => {
    cachedUserId = session?.user?.id ?? null;
  });
}

const SESSION_KEY = 'funnel_session_id';
const SESSION_START_KEY = 'funnel_session_start';
// Once-per-session sentinel keys. Stored alongside the session id so that
// when the session id rotates these naturally reset. We use a single
// JSON-encoded set to avoid spamming localStorage with one key per event.
const SESSION_FIRED_KEY = 'funnel_session_fired';

function getSessionId(): string {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
        ? crypto.randomUUID()
        : `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(SESSION_KEY, id);
      // Stamp the session start so session_ended can compute duration_ms
      // without us storing per-event timestamps server-side.
      localStorage.setItem(SESSION_START_KEY, String(Date.now()));
      // Reset the once-per-session sentinels alongside a fresh id.
      localStorage.removeItem(SESSION_FIRED_KEY);
      localStorage.removeItem(SESSION_FLAGS_KEY);
    }
    return id;
  } catch {
    // localStorage unavailable -> ephemeral per-call session id
    return `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }
}

/**
 * Returns the session start epoch-ms, or null if unavailable.
 * Used by session_ended to compute duration without a separate store.
 */
export function getSessionStartMs(): number | null {
  try {
    const raw = localStorage.getItem(SESSION_START_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

// Sticky per-session flags consumed by session_ended. These let analytics
// filter "bounce on landing" sessions (3s, no app interaction) from
// "designer abandonment" sessions (60s+, but the user actually opened the
// product). Cleared alongside the session id rotation.
const SESSION_FLAGS_KEY = 'funnel_session_flags';

export function markSessionFlag(flag: string): void {
  try {
    getSessionId();
    const raw = localStorage.getItem(SESSION_FLAGS_KEY);
    const flags = raw ? (JSON.parse(raw) as Record<string, true>) : {};
    if (flags[flag]) return;
    flags[flag] = true;
    localStorage.setItem(SESSION_FLAGS_KEY, JSON.stringify(flags));
  } catch { /* localStorage unavailable */ }
}

export function getSessionFlags(): Record<string, true> {
  try {
    const raw = localStorage.getItem(SESSION_FLAGS_KEY);
    return raw ? (JSON.parse(raw) as Record<string, true>) : {};
  } catch {
    return {};
  }
}

/**
 * Once-per-session gate. Returns true the first time you call it with a
 * given key during the current session; false on every subsequent call.
 * Used for landing_viewed / referrer_classified / returning_session so they
 * fire exactly once per session, even if the user navigates many times.
 */
export function fireOncePerSession(key: string): boolean {
  try {
    // Make sure a session id exists so SESSION_FIRED_KEY rotation is keyed
    // to the same session as the events themselves.
    getSessionId();
    const raw = localStorage.getItem(SESSION_FIRED_KEY);
    const fired = raw ? (JSON.parse(raw) as Record<string, true>) : {};
    if (fired[key]) return false;
    fired[key] = true;
    localStorage.setItem(SESSION_FIRED_KEY, JSON.stringify(fired));
    return true;
  } catch {
    // If localStorage is unavailable, conservatively allow the event — we'd
    // rather over-count than miss the signal entirely.
    return true;
  }
}

/**
 * Once-per-device gate (persists indefinitely until localStorage is cleared).
 * Distinct from fireOncePerSession — useful for "user has placed their FIRST
 * camera ever" semantics where re-entering the designer in a new session
 * should NOT re-fire. Returns true the first time, false thereafter.
 */
export function fireOnceEver(key: string): boolean {
  const storageKey = `funnel_once_ever_${key}`;
  try {
    if (localStorage.getItem(storageKey)) return false;
    localStorage.setItem(storageKey, String(Date.now()));
    return true;
  } catch {
    // localStorage unavailable — be conservative and allow the event so
    // we under-count rather than risk silent zero on private-mode browsers.
    return true;
  }
}

// Hostname → bucket map for the referrer classifier. Anchored to the EXACT
// hostname (or a `.foo.com` suffix) so substrings like `chatgpt.com`
// stop matching `t.co`. The previous substring approach mis-classified any
// `*t.com` domain as Twitter.
const REFERRER_RULES: Array<{ source: ReferrerSource; hosts: string[] }> = [
  { source: 'organic_google', hosts: ['google.', 'googleusercontent.', 'googleweblight.'] },
  { source: 'organic_bing', hosts: ['bing.', 'msn.'] },
  { source: 'reddit', hosts: ['reddit.', 'redd.it'] },
  { source: 'linkedin', hosts: ['linkedin.', 'lnkd.in'] },
  { source: 'twitter', hosts: ['twitter.com', 'mobile.twitter.com', 'x.com', 't.co'] },
  { source: 'facebook', hosts: ['facebook.', 'fb.com', 'fb.me', 'm.facebook.com'] },
  { source: 'youtube', hosts: ['youtube.', 'youtu.be'] },
  { source: 'github', hosts: ['github.com', 'github.io'] },
  { source: 'chatgpt', hosts: ['chatgpt.com', 'chat.openai.com'] },
  { source: 'claude_ai', hosts: ['claude.ai'] },
  { source: 'perplexity', hosts: ['perplexity.ai'] },
  { source: 'gemini', hosts: ['gemini.google.com'] },
  { source: 'copilot_ms', hosts: ['copilot.microsoft.com'] },
];

type ReferrerSource =
  | 'organic_google' | 'organic_bing' | 'reddit' | 'linkedin' | 'twitter'
  | 'facebook' | 'youtube' | 'github' | 'chatgpt' | 'claude_ai' | 'perplexity'
  | 'gemini' | 'copilot_ms'
  | 'blog_internal' | 'direct' | 'other';

// UTM-source → ReferrerSource mapping for AEO/LLM-origin traffic. Some
// LLMs strip Referer (privacy default) so the only signal is utm_source=
// in the campaign URL embedded in the bot's answer. Upgrading the bucket
// from 'direct'/'other' to a typed AEO source means /admin/funnel queries
// don't need a JOIN on utm_source — the same `source` column tells the
// whole story. Match patterns are case-insensitive substrings on utm_source.
const AEO_UTM_PATTERNS: Array<{ pattern: RegExp; source: ReferrerSource }> = [
  { pattern: /(?:^|[^a-z])(chatgpt|openai)(?:$|[^a-z])/i, source: 'chatgpt' },
  { pattern: /(?:^|[^a-z])(claude|anthropic)(?:$|[^a-z])/i, source: 'claude_ai' },
  { pattern: /(?:^|[^a-z])perplexity(?:$|[^a-z])/i, source: 'perplexity' },
  { pattern: /(?:^|[^a-z])(gemini|bard)(?:$|[^a-z])/i, source: 'gemini' },
  { pattern: /(?:^|[^a-z])copilot(?:$|[^a-z])/i, source: 'copilot_ms' },
];

function classifyAeoUtm(utmSource?: string): ReferrerSource | null {
  if (!utmSource) return null;
  for (const { pattern, source } of AEO_UTM_PATTERNS) {
    if (pattern.test(utmSource)) return source;
  }
  return null;
}

/**
 * Coarse referrer classification. Used once per session by RouteTracker
 * so analytics can group "where did this signup come from" without us
 * leaking raw referrer URLs into funnel_events. Returns 'direct' for
 * SSR/no-referrer, since both render the same downstream behaviour.
 *
 * Match strategy: URL.hostname compared per-host, exact-or-suffix. Previous
 * substring matcher had a t.co false-positive that mis-classified every
 * *t.com referrer (chatgpt.com, support.com, …) as Twitter.
 */
export function classifyReferrer(): {
  source: ReferrerSource;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
} {
  if (typeof window === 'undefined') return { source: 'direct' };
  try {
    const params = new URLSearchParams(window.location.search);
    const utm_source = params.get('utm_source') || undefined;
    const utm_medium = params.get('utm_medium') || undefined;
    const utm_campaign = params.get('utm_campaign') || undefined;
    const meta = { utm_source, utm_medium, utm_campaign };

    // AEO/LLM origin via utm_source — runs BEFORE referrer parsing because
    // LLMs strip Referer by default; without this branch chatgpt/claude/etc.
    // UTM-tagged links would all bucket as 'direct'/'other'.
    const aeoSource = classifyAeoUtm(utm_source);
    if (aeoSource) return { source: aeoSource, ...meta };

    const refRaw = document.referrer || '';
    if (!refRaw) return { source: 'direct', ...meta };

    let host: string;
    try {
      host = new URL(refRaw).hostname.toLowerCase();
    } catch {
      // Malformed referrer (rare browsers / extensions). Bucket as 'other'
      // rather than risk false-positive substring matches downstream.
      return { source: 'other', ...meta };
    }

    if (!host) return { source: 'other', ...meta };

    // Internal blog → designer attribution: most common signup path.
    if (host === window.location.hostname.toLowerCase()) {
      return { source: 'blog_internal', ...meta };
    }

    // Exact match or proper subdomain match (`reddit.com`, `old.reddit.com`).
    // Hosts ending in `.` in the rules table are treated as legacy substring
    // matchers — covers `google.co.uk` / `google.pl` variants without
    // listing every TLD. Non-trailing entries match exactly or as suffix.
    for (const rule of REFERRER_RULES) {
      for (const h of rule.hosts) {
        if (h.endsWith('.')) {
          const stem = h.slice(0, -1); // 'google'
          if (host === stem || host.endsWith(`.${stem}`) || host.includes(`.${stem}.`)) {
            return { source: rule.source, ...meta };
          }
        } else if (host === h || host.endsWith(`.${h}`)) {
          return { source: rule.source, ...meta };
        }
      }
    }
    return { source: 'other', ...meta };
  } catch {
    return { source: 'direct' };
  }
}

/**
 * Read first-touch landing context persisted by RouteTracker on
 * LANDING_VIEWED. Returns an object that's safe to pass into
 * supabase.auth.signUp({ options: { data: ... } }) or
 * supabase.auth.updateUser({ data: ... }) so signup-time attribution
 * survives the auth round-trip. Returns null fields when localStorage
 * has nothing — pass undefined values, not nulls, to keep metadata clean.
 *
 * Why: 2026-05-21 SEO audit showed 93% of signups had no client-side
 * landing path. server-trigger emits signup_complete with path
 * '/auth/server-trigger' — useless for SEO attribution. This bridges
 * client landing → user_metadata so we can query it later.
 */
export function getLandingAttribution(): Record<string, string> {
  const out: Record<string, string> = {};
  try {
    const path = localStorage.getItem('funnel_landing_path');
    if (path) out.landing_path = path;
    const referrer = localStorage.getItem('funnel_landing_referrer');
    if (referrer) out.landing_referrer = referrer;
    const utmRaw = localStorage.getItem('funnel_landing_utm');
    if (utmRaw) {
      try {
        const utm = JSON.parse(utmRaw) as Record<string, string>;
        for (const [k, v] of Object.entries(utm)) {
          if (typeof v === 'string') out[`landing_${k}`] = v.slice(0, 120);
        }
      } catch { /* malformed JSON — skip */ }
    }
  } catch { /* localStorage unavailable */ }
  return out;
}

/**
 * Track a funnel event. Fire-and-forget, never throws.
 *
 * Drops the event silently if neither consent nor auth is available.
 */
export function trackFunnelEvent(
  eventName: string,
  properties?: Record<string, unknown>,
): void {
  if (typeof window === 'undefined') return;

  // Drop dev / local-build traffic so prod analytics aren't polluted by
  // localhost:8080 sessions. We've been seeing self-pollution in funnel data
  // because the dev server also writes to the prod Supabase table.
  const host = window.location.hostname.toLowerCase();
  if (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '0.0.0.0' ||
    host.endsWith('.local') ||
    host.endsWith('.test')
  ) {
    return;
  }

  // Use the synchronously-cached user id (kept fresh by onAuthStateChange
  // subscription above) instead of `await supabase.auth.getSession()` per
  // event — eliminates the supabase-js mutex contention that previously
  // serialized analytics during rapid clicks.
  ensureUserIdCacheInit();
  const userId = cachedUserId;

  // Gate: authenticated users always allowed (legitimate interest);
  // anon users require explicit cookie consent.
  if (!userId && !hasConsentedToCookies()) return;

  void (async () => {
    try {
      const row = {
        user_id: userId,
        session_id: getSessionId(),
        event_name: eventName.slice(0, 64),
        properties: properties ?? {},
        path: window.location.pathname + window.location.search,
        referrer: document.referrer || null,
        user_agent: navigator.userAgent.slice(0, 500),
      };

      await supabase.from('funnel_events').insert(row);
    } catch {
      // best-effort, swallow all errors
    }
  })();
}
